<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = computed(() => getters['teamleaders/getUIFlags'].value || {});
const teamleaders = computed(
  () => getters['teamleaders/getTeamleaders'].value || []
);

const isLoading = computed(
  () => uiFlags.value.isFetching || uiFlags.value.isCreating
);
const isEmpty = computed(() => teamleaders.value.length === 0);
const isConnected = computed(() => teamleaders.value.length > 0);
const teamleader = computed(() => teamleaders.value[0] || null);

onMounted(() => {
  store.dispatch('teamleaders/get');
});

const handleConnect = async () => {
  try {
    await store.dispatch('teamleaders/authorize');
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.TEAMLEADER.CONNECT_ERROR')
    );
  }
};

const handleDisconnect = async () => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('INTEGRATION_SETTINGS.TEAMLEADER.DISCONNECT.CONFIRM'))) {
    try {
      await store.dispatch('teamleaders/delete', teamleader.value.id);
      useAlert(t('INTEGRATION_SETTINGS.TEAMLEADER.DISCONNECT.SUCCESS'));
    } catch (error) {
      useAlert(
        error?.message || t('INTEGRATION_SETTINGS.TEAMLEADER.DISCONNECT.ERROR')
      );
    }
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.TEAMLEADER.HEADER')"
    :button-label="
      isConnected
        ? $t('INTEGRATION_SETTINGS.TEAMLEADER.DISCONNECT_BUTTON')
        : $t('INTEGRATION_SETTINGS.TEAMLEADER.CONNECT_BUTTON')
    "
    @click="isConnected ? handleDisconnect() : handleConnect()"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      icon="i-lucide-briefcase"
      :title="$t('INTEGRATION_SETTINGS.TEAMLEADER.EMPTY_STATE')"
      :description="$t('INTEGRATION_SETTINGS.TEAMLEADER.DESCRIPTION')"
      :button-label="$t('INTEGRATION_SETTINGS.TEAMLEADER.CONNECT_BUTTON')"
      @action="handleConnect"
    />

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        icon="i-lucide-briefcase"
        icon-class="bg-gradient-to-br from-n-green-500 to-n-emerald-600"
        :title="
          teamleader?.account_name ||
          teamleader?.settings?.account_name ||
          $t('INTEGRATION_SETTINGS.TEAMLEADER.CONNECTED')
        "
        :description="
          $t('INTEGRATION_SETTINGS.TEAMLEADER.CONNECTED_DESCRIPTION')
        "
      />
    </div>
  </SettingsLayout>
</template>
